import {
  ApiOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  HomeOutlined,
  QuestionCircleOutlined,
  RollbackOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons';
import { ConditionsIcon, XRayIconSquare } from '@mm/icons';
import { SubscriptionFeatureFlag } from '@mm/state/controllers/FeatureFlags';
import { CompanyLevelFeatureFlag } from '@mm/state/model/CompanyModel';
import { ReactComponent as AudienceIcon } from 'assets/audiences-icon-left-menu.svg';
import { ReactComponent as IdcardOutlined } from 'assets/id-card.svg';
import { ReactComponent as ApplicationsIcon } from 'assets/more-icon-left-menu.svg';
import classNames from 'classnames';
import React from 'react';
import { HELP_BETA } from '../../common/constants/externalRoutes';

export type SidebarMenuDefinition = {
  id: string;
  name: string;
  icon?: (isActive: boolean) => React.ReactNode;
  description?: string;
  url: string;
  childRoutes?: string[];
  mustBeSysAdmin: boolean;
  hideForRole?: string[];
  featureFlag?: CompanyLevelFeatureFlag | SubscriptionFeatureFlag;
  hideFeatureFlag?: CompanyLevelFeatureFlag | SubscriptionFeatureFlag;
  historyState?: any;
  externalLink?: boolean;
};

export type SidebarTitleDefinition = {
  id: string;
  text: string;
  description?: string;
  icon?: (isActive: boolean) => React.ReactNode;
  menuItems: SidebarMenuDefinition[];
  category?: 'tools' | 'contacts' | 'optimize';
  url?: string;
  hideOnMobile: boolean;
  featureFlag?: CompanyLevelFeatureFlag | SubscriptionFeatureFlag;
  hideFeatureFlag?: CompanyLevelFeatureFlag | SubscriptionFeatureFlag;
  externalLink?: boolean;
};

export const SIDEBAR_MENU_GROUPS = ['tools', 'contacts', 'optimize'];

const iconStyleProps = (isActive: boolean) => ({
  width: 20,
  height: 20,
  fontSize: 20,
  color: isActive ? '#262626' : '#8CA0B5',
});

export const SIDEBAR_MENU: SidebarTitleDefinition[] = [
  {
    id: 'DASHBOARD',
    text: 'Dashboard',
    url: '/dashboard',
    hideOnMobile: false,
    icon: (isActive: boolean) => (
      <HomeOutlined style={iconStyleProps(isActive)} />
    ),
    menuItems: [],
  },
  {
    id: 'XRAY',
    text: 'X-Ray Pixel',
    description: 'Gather leads from your website traffic',
    url: '/x-ray',
    hideOnMobile: false,
    icon: (isActive: boolean) => (
      <XRayIconSquare color={isActive ? '#262626' : '#8CA0B5'} size={20} />
    ),
    menuItems: [],
    category: 'tools',
  },
  {
    id: 'RESTORE',
    text: 'Restore',
    url: '/restore-ads',
    icon: (isActive: boolean) => (
      <RollbackOutlined style={iconStyleProps(isActive)} />
    ),
    hideOnMobile: false,
    menuItems: [],
    category: 'tools',
  },
  {
    id: 'SIGNAL',
    text: 'Signal',
    description: 'Superpower your flows with event data.',
    url: '/signal',
    hideOnMobile: false,
    icon: (isActive: boolean) => (
      <CheckCircleOutlined style={iconStyleProps(isActive)} />
    ),
    menuItems: [],
    category: 'tools',
  },
  {
    id: 'INTEGRATIONS',
    text: 'Integrations',
    description:
      'Connect your CRM and other platforms directly to Customers.ai',
    url: '/integrations',
    icon: (isActive: boolean) => (
      <ApiOutlined style={iconStyleProps(isActive)} />
    ),
    menuItems: [
      {
        id: 'email',
        name: 'Email Outbound Tools',
        url: '/integrations/email',
        historyState: { kind: 'email' },
        featureFlag: 'nativeIntegrations',
        mustBeSysAdmin: false,
      },
      {
        id: 'native-integrations',
        name: 'CRM connections',
        url: '/integrations/native-integrations',
        historyState: { kind: 'native-integrations' },
        featureFlag: 'nativeIntegrations',
        mustBeSysAdmin: false,
      },
      {
        id: 'contact-suppression',
        name: 'Contact Supression',
        url: '/integrations/contact-suppression',
        historyState: { kind: 'contact-suppression' },
        featureFlag: 'contactSuppression',
        mustBeSysAdmin: false,
      },
      {
        id: 'unsubscribe-list',
        name: 'Unsubscribe List',
        url: '/integrations/unsubscribe-list',
        historyState: { kind: 'unsubscribe-list' },
        featureFlag: 'contactSuppression',
        mustBeSysAdmin: false,
      },
      {
        id: 'facebook',
        name: 'Facebook',
        url: '/integrations/facebook',
        historyState: { kind: 'facebook' },
        featureFlag: 'fbRestore',
        mustBeSysAdmin: false,
      },
      {
        id: 'google-ads',
        name: 'Google Ads',
        url: '/integrations/google-ads',
        historyState: { kind: 'google-ads' },
        featureFlag: 'fbRestore',
        mustBeSysAdmin: false,
      },
      {
        id: 'notifications',
        name: 'Notifications',
        url: '/integrations/notifications',
        historyState: { kind: 'notifications' },
        featureFlag: 'nativeIntegrations',
        mustBeSysAdmin: false,
      },
      {
        id: 'xray',
        name: 'X-Ray Pixel',
        url: '/integrations/xray',
        historyState: { kind: 'xray' },
        featureFlag: 'xray',
        mustBeSysAdmin: false,
      },
    ],
    hideOnMobile: false,
    category: 'tools',
  },

  {
    id: 'AUTOMATIONS',
    text: 'Automations',
    url: '/automations',
    description: 'Create custom outbound automations.',
    hideOnMobile: false,
    icon: (isActive?: boolean) => (
      <ConditionsIcon color={isActive ? '#262626' : '#8CA0B5'} size={20} />
    ),
    menuItems: [],
    featureFlag: 'automations' as CompanyLevelFeatureFlag,
    category: 'tools',
  },
  {
    id: 'CONTACTS',
    text: 'My Leads',
    description:
      'All of your contacts in one place. Sort, filter, and create audiences.',
    url: '/contacts',
    hideOnMobile: false,
    icon: (isActive: boolean) => (
      <IdcardOutlined
        style={iconStyleProps(isActive)}
        className={classNames('sidebar-menu-group__icon-audiences', {
          'sidebar-menu-group__icon-audiences-black': isActive,
        })}
      />
    ),
    menuItems: [],
    category: 'contacts',
  },
  {
    id: 'AUDIENCES',
    text: 'Audiences',
    description: 'Subsets of your leads filtered by intent.',
    url: '/audiences',
    hideOnMobile: false,
    icon: (isActive: boolean) => (
      <AudienceIcon
        style={iconStyleProps(isActive)}
        className={classNames('sidebar-menu-group__icon-audiences', {
          'sidebar-menu-group__icon-audiences-black': isActive,
        })}
      />
    ),
    menuItems: [],
    category: 'contacts',
  },

  {
    id: 'ANALYTICS',
    text: 'Analytics',
    url: '/xray-analytics',
    icon: (isActive: boolean) => (
      <BarChartOutlined style={iconStyleProps(isActive)} />
    ),
    category: 'optimize',
    menuItems: [
      {
        id: 'XRAY_ANALYTICS',
        name: 'X-Ray Pixel',
        url: '/xray-analytics',
        featureFlag: 'analytics',
        mustBeSysAdmin: false,
      },
      {
        id: 'EMAIL_ANALYTICS',
        name: 'Outreach',
        url: '/outreach-analytics',
        featureFlag: 'analytics',
        mustBeSysAdmin: false,
      },
      {
        id: 'FB_RESTORE_ANALYTICS',
        name: 'Restore',
        url: '/restore-analytics',
        mustBeSysAdmin: false,
      },
      {
        id: 'DEMOGRAPHIC_ANALYTICS',
        name: 'Demographic Data',
        url: '/demographic-analytics',
        featureFlag: 'analytics',
        mustBeSysAdmin: false,
      },
    ],
    hideOnMobile: false,
  },
  {
    id: 'DIRECTORY',
    text: 'Directory',
    url: '/b2b-leads',
    icon: (isActive: boolean) => (
      <UsergroupAddOutlined style={iconStyleProps(isActive)} />
    ),
    category: 'optimize',
    menuItems: [
      {
        id: 'VOYAGER',
        name: 'B2B Leads',
        description: 'Purchase targeted B2B leads.',
        url: '/b2b-leads',
        mustBeSysAdmin: false,
      },
      {
        id: 'MERCURY',
        name: 'Consumer Directory',
        description: 'Purchase targeted consumer leads.',
        url: '/mercury',
        mustBeSysAdmin: false,
      },
    ],
    hideOnMobile: false,
  },
  {
    id: 'OTHER',
    text: 'Old Features',
    url: '/inbox',
    hideOnMobile: false,
    icon: (isActive: boolean) => (
      <ApplicationsIcon
        style={iconStyleProps(isActive)}
        className={classNames('sidebar-menu-group__icon-audiences', {
          'sidebar-menu-group__icon-audiences-black': isActive,
        })}
      />
    ),
    menuItems: [
      {
        id: 'INBOX',
        name: 'Inbox',
        url: '/live-chat',
        mustBeSysAdmin: false,
        hideForRole: ['monitor_leads'],
      },
      {
        id: 'CHAT_STARTERS',
        name: 'Chat Starters',
        url: '/chat-starters',
        hideForRole: ['monitor_leads', 'live_chat_agent'],
        mustBeSysAdmin: false,
      },
      {
        id: 'DIALOGUES',
        name: 'Dialogue Library',
        url: '/build',
        hideForRole: ['monitor_leads', 'live_chat_agent'],
        mustBeSysAdmin: false,
      },
      {
        id: 'TRIGGER',
        name: 'Q&A',
        url: '/trigger',
        hideForRole: ['monitor_leads', 'live_chat_agent'],
        mustBeSysAdmin: false,
      },
      {
        id: 'CAMPAIGNS',
        name: 'Campaigns',
        url: '/campaigns',
        hideForRole: ['monitor_leads', 'live_chat_agent'],
        mustBeSysAdmin: false,
      },
      {
        id: 'RULES',
        name: 'Automation Rules',
        url: '/automation-rules',
        hideForRole: ['monitor_leads', 'live_chat_agent'],
        mustBeSysAdmin: false,
      },
      {
        id: 'BOT_ANALYTICS',
        name: 'Bot Analytics',
        url: '/bot-analytics',
        mustBeSysAdmin: false,
      },
      {
        id: 'ADS_ANALYTICS',
        name: 'Ads Analytics',
        url: '/ads-analytics',
        featureFlag: 'analyticsAds',
        mustBeSysAdmin: false,
      },
      {
        id: 'SETTINGS',
        name: 'Settings',
        url: '/settings',
        mustBeSysAdmin: false,
      },
    ],
    category: 'optimize',
  },

  {
    id: 'HELP',
    text: 'Help',
    url: HELP_BETA,
    externalLink: true,
    hideOnMobile: false,
    icon: (isActive: boolean) => (
      <QuestionCircleOutlined style={iconStyleProps(isActive)} />
    ),
    menuItems: [],
    category: 'optimize',
  },
];
